<template>
  <div id="app">
    <a-config-provider v-if="isLoading" :locale="locale" :getPopupContainer="getPopupContainer">
      <a-spin v-bind="loadingProps" class="page-loading">
        <router-view />
      </a-spin>
    </a-config-provider>

    <!-- loading -->
    <div v-else class="app-loading">
      <a-spin class="app-loading__spin" />
    </div>
  </div>
</template>

<script>
import Vue from 'vue'
import zhCN from 'ant-design-vue/lib/locale-provider/zh_CN'
import _zoom from '@/utils/zoom'
import _util from '@/utils/_util'
import Base64 from '@/utils/base64'
import { request, setConfig } from '@/utils/request'
import { readLocal, readSion, saveLocal, saveSion } from '@/view/public/common'
import axios from 'axios'
import dayjs from 'dayjs'
import { createWatermark, removeWatermark } from '@/utils/watermark'
import { getWorkgroupList } from '@/apis/inputSpace/infoCategory'
import { removeActivityListeners, setupActivityListeners } from '@/activityListeners'

const baseFunc = new Base64()
const { getQueryString } = _util

export default {
  name: 'APP',
  data() {
    return {
      publicChannel: null,
      locale: zhCN,
      isLoading: false,
      projectId: null,
      planId: null,
      spyId: null,
      curRouter: null,
      loadingProps: {
        spinning: false
      },
      needWatermarkPage: ['/infoInput/entryDetails', '/infoInput/transferDetails']
    }
  },

  computed: {
    // 开发环境
    isDev() {
      return process.env.NODE_ENV === 'development'
    },
    // 公文服务地址
    curUrl() {
      return window.location.origin + window.location.pathname.replace(/\/[^/]*$/, '')
    },
    // 当前路由
    curPathRouter() {
      const hashPath = window.location.hash
      const index = hashPath.indexOf('?')
      return index > -1 ? hashPath.substring(1, index) : hashPath.substring(1)
    },
    // 地址栏加密参数解析
    urlParams() {
      const params = getQueryString('params')
      const base64Params = baseFunc.decode(params)
      params && saveSion('urlParams', JSON.parse(base64Params))
      return params ? JSON.parse(base64Params) : {}
    },
    // 明文方式获取token
    getPlanToken() {
      return getQueryString('token') || readSion('token')
    },
    // 平台类型
    getClientType() {
      return getQueryString('client')
    }
  },

  watch: {
    // 监听地址栏参数变化，重新设置token
    $route: {
      deep: true,
      handler(r) {
        const { params, token } = r.query || {}
        if (params || token) {
          const { token: urlToken, bMenuCode } = params && JSON.parse(baseFunc.decode(params))
          const newSpyId = bMenuCode || readSion('spyId')
          this.$store.commit('setSpyId', newSpyId)
          this.setToken(token || urlToken, readSion('planCode'))
        }
      }
    }
  },

  beforeCreate() {
    Vue.prototype.$app = this
  },
  created() {
    this.publicChannel = new BroadcastChannel('public_channel')
  },
  async mounted() {
    try {
      const token = this.urlParams.token || this.getPlanToken
      const clientType = this.urlParams.client || this.getClientType || readLocal('client')
      const taskInformUrl = decodeURIComponent(this.curUrl) // 获取跳转链接地址
      const newSpyId = this.urlParams.bMenuCode || readSion('spyId')
      const serverTime = this.urlParams.serverTime || readSion('server_time') // 服务器时间
      const { projectId, planId, planName, projectName, planCode } = this.urlParams || {}

      this.setToken(token, planCode) // 设置token
      saveLocal('client', clientType)
      saveSion('planCode', planCode)
      saveSion('taskInformUrl', taskInformUrl)
      saveSion('server_time', serverTime)
      this.$store.commit('setSpyId', newSpyId)
      this.$store.commit('setProjectPlan', { projectId, planId, planName, projectName, planCode })

      await this.getUserInfo()
      this.createWatermarkByWeb()
      // await this.getProjectPlan(newSpyId)
    } finally {
      this.isLoading = true
    }

    setupActivityListeners()
    this.publicChannel.onmessage = this.onChannelMessage

    _zoom() // 缩放控制
  },
  beforeDestroy() {
    localStorage.clear()
    sessionStorage.clear()

    removeActivityListeners()
    this.publicChannel.close()
  },

  methods: {
    // 监听标签页通信数据
    onChannelMessage({ data }) {
      if (!data) return // 如果没有数据则直接返回

      // 判断是否是当前用户
      const userInfo = readSion('userInfo') || {}
      if (data.userId && data.userId !== userInfo.userId) return // 如果用户ID不匹配则直接返回

      // 定义通知函数
      const typeMap = {
        noToken: () => this.tokenFailure()
      }

      // 处理不同类型的消息
      if (typeMap[data.type]) {
        typeMap[data.type]()
      }
    },

    // token失效处理
    tokenFailure() {
      const { userId } = readSion('userInfo') || {}
      const params = { type: 'noToken', code: 401, userId }

      this.publicChannel.postMessage(params)
      window.top && window.top.postMessage(params, '*')
      window.open('/#/web/noauth', '_self')
    },

    // 获取项目计划下当前用户所属权限的工作组集合
    async getWorkgroupListByOwn() {
      try {
        this.$setLoading({ tip: '' })
        const { userId } = readSion('userInfo') || {}
        if (!userId) return // 没有用户Id

        const { spyId } = this.$store.state
        const params = {
          current: 1,
          pageSize: 9999,
          uesrId: userId,
          spyRightCode: spyId
        }
        const res = await getWorkgroupList(params)
        if (res.result) {
          this.$store.commit('setOwnWorkgroupList', res.data || [])
        } else {
          this.$message.warn(res.message || '获取工作组列表失败')
        }
      } catch (e) {
        console.error('e', e)
      } finally {
        this.$setLoading(false)
      }
    },

    // web端才创建水印
    createWatermarkByWeb() {
      const isApp = readLocal('client') === 'APP'
      if (isApp) return // app端不创建水印

      // 指定页面创建水印
      if (this.needWatermarkPage.includes(this.curPathRouter)) {
        const curTime = readSion('server_time')
        const curDateTime = dayjs(curTime, 'YYYYMMDD').format('YYYYMMDD')
        const { realName, orgName, userType, mobileNo } = readSion('userInfo') || {}
        const userName = userType === '1' ? mobileNo : realName // 临时账号展示手机号
        const text = `${userName}（${orgName}）${curDateTime}`
        createWatermark(text)
      }
    },

    // 设置token
    setToken(token = '', planCode = '') {
      this.$bus.$emit('refreshToken', token)
      saveSion('token', token)
      setConfig(G.dataService, token, '', planCode)
    },

    // 获取用户信息
    async getUserInfo() {
      let url = '/api/orgproject/user/getCurrentUserInfo'
      await request(url, {}).then(res => {
        const BASE_USER_INFO = JSON.parse(sessionStorage.getItem('BASE_PC_USER_ORG_INFO'))
        const USER_INFO_DATA = res.data.data || BASE_USER_INFO || {}
        saveSion('userInfo', USER_INFO_DATA)
      })
    },

    // 获取项目计划
    async getProjectPlan(spyId) {
      let planId = ''
      let projectId = ''
      const url = '/api/orgproject/project/myAllProject'
      await request(url, { spyId })
        .then(async res => {
          const { data } = res
          if (data.result) {
            if (data.data && data.data.length) {
              const isExist = this.handelCheckIdExist(data.data, planId) // 判断门户传入项目计划ID是否在数据中存在
              // if (!isExist && !data.data.length) return // 不存在项目计划ID且数据为空
              // 处理不存在的计划
              if (planId && !isExist) {
                this.handelNoExistPlan(data.data)
                return
              }
              // 计划ID存在且有数据
              if ((planId !== 'undefined' || planId) && isExist) {
                this.planId = planId
                data.data.forEach(p => {
                  p.children.forEach(o => {
                    if (o.id === planId) {
                      this.projectId = p.id
                    }
                  })
                })
              } else {
                // 无项目ID
                if (!projectId) {
                  this.handelNoExistPlan(data.data) // 处理不存在的项目计划
                } else {
                  // 有项目ID
                  this.projectId = projectId
                  data.data.forEach(p => {
                    if (p.id === projectId) {
                      let enableData = this.getNowPlanIdData(p.children)
                      this.planId = enableData.length ? enableData[0].id : p.children[0].id
                    }
                  })
                }
              }
            }

            this.$store.commit('setPlanOptions', data.data) // 存入项目计划列表数据
          } else {
            this.$message.warn(data.message)
          }
        })
        .catch(err => {
          this.$message.warn(err.message)
        })
    },

    // 处理不存在的计划
    handelNoExistPlan(data) {
      let allPlanData = []
      data.forEach(p => {
        if (p.children) {
          p.children.forEach(o => {
            allPlanData.push(o)
          })
        }
      })
      allPlanData = this.getNowPlanIdData(allPlanData)
      this.planId = allPlanData[0].id
      data.forEach(p => {
        if (p.children) {
          p.children.forEach(o => {
            if (o.id === this.planId) {
              this.projectId = p.id
            }
          })
        }
      })
      // 存入项目计划ID
      saveSion('planId', this.planId)
      saveSion('projectId', this.projectId)
      this.$store.commit('setProjectPlan', {
        projectId: this.projectId,
        planId: this.planId
      })
    },

    // 获取最新启用的计划ID数组
    getNowPlanIdData(data) {
      // 按照最新计划排序
      let newData = data.sort((a, b) => {
        let aTimestamp = new Date(a.createTime).getTime()
        let bTimestamp = new Date(b.createTime).getTime()
        return bTimestamp - aTimestamp
      })
      // 过滤启用的计划
      const filterData = newData.filter(item => String(item.isEnable) === '1')
      return filterData.length ? filterData : newData
    },

    // 判断门户传入项目计划ID是否在数据中存在
    handelCheckIdExist(data, id) {
      if (!data.length) return false
      let isFlag = false
      data.forEach(p => {
        if (p.id === id) {
          isFlag = true
        } else {
          p.children.forEach(o => {
            if (o.id === id) {
              isFlag = true
            }
          })
        }
      })
      return isFlag
    },

    // 解决Select，DatePicker，Cascader等组件选项框随页面滚动分离问题
    getPopupContainer(node) {
      if (node) return node.parentNode
      return document.body
    }
  }
}
</script>

<style lang="scss">
#app {
  height: 100%;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;

  .ant-spin,
  .ant-spin-show-text {
    max-height: inherit !important;
  }
}

.ant-spin-container,
.ant-spin-nested-loading {
  height: 100%;
}

.app-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  width: 100%;
  height: 100%;

  .app-loading__spin {
    margin: 0 auto;
  }
}

//.page-loading {
//  background: #dfdfdd;
//
//  .ant-spin {
//    top: 50% !important;
//    left: 50% !important;
//    transform: translate(-50%, -50%);
//    width: 300px !important;
//    height: 130px !important;
//    background: #fff;
//  }
//}
</style>



// WEBPACK FOOTER //
// src/App.vue